/**
 * Mock Language Service Provider
 *
 * A language service provider that uses the mock language service backend
 * for deterministic completions and diagnostics in E2E tests.
 */

import type {
  LanguageServiceProvider,
  LanguageServiceCompletionResult,
  LanguageServicePosition,
  LanguageServiceCompletionContext,
  DiagnosticsListener,
  LanguageServiceConnectionInfo,
} from './languageServiceProvider';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';

// Store diagnostics listeners
const diagnosticsListeners: DiagnosticsListener[] = [];

// Track registered documents
const documents = new Map<string, { text: string; connectionId?: string; database?: string }>();

/**
 * Mock Language Service Provider
 * Uses IPC to communicate with the mock language service in the main process
 */
export const mockLanguageServiceProvider: LanguageServiceProvider = {
  engine: 'mock',

  ensureInitialized: async (): Promise<void> => {
    // Mock is always ready
  },

  registerDocument: async (uri: string, text: string): Promise<boolean> => {
    documents.set(uri, { text });
    return true;
  },

  updateDocument: async (uri: string, text: string): Promise<boolean> => {
    const doc = documents.get(uri);
    if (doc) {
      doc.text = text;
    } else {
      documents.set(uri, { text });
    }
    return true;
  },

  closeDocument: async (uri: string): Promise<boolean> => {
    documents.delete(uri);
    return true;
  },

  connectForLanguageServices: async (
    uri: string,
    connectionInfo: LanguageServiceConnectionInfo
  ): Promise<boolean> => {
    const doc = documents.get(uri);
    if (doc) {
      doc.connectionId = connectionInfo.connectionId;
      doc.database = connectionInfo.database;
    }
    return true;
  },

  disconnectForLanguageServices: async (uri: string): Promise<boolean> => {
    const doc = documents.get(uri);
    if (doc) {
      doc.connectionId = undefined;
      doc.database = undefined;
    }
    return true;
  },

  onDiagnostics: (listener: DiagnosticsListener): (() => void) => {
    diagnosticsListeners.push(listener);
    return () => {
      const index = diagnosticsListeners.indexOf(listener);
      if (index >= 0) {
        diagnosticsListeners.splice(index, 1);
      }
    };
  },

  getCompletionItems: async (
    uri: string,
    position: LanguageServicePosition,
    context?: LanguageServiceCompletionContext
  ): Promise<LanguageServiceCompletionResult> => {
    void context;
    const defaultCompletions: Array<{ label: string; kind: number; detail: string; insertText?: string }> = [
      { label: 'SELECT', kind: 14, detail: 'SQL keyword', insertText: 'SELECT' },
      { label: 'FROM', kind: 14, detail: 'SQL keyword', insertText: 'FROM' },
      { label: 'WHERE', kind: 14, detail: 'SQL keyword', insertText: 'WHERE' },
      { label: 'INSERT', kind: 14, detail: 'SQL keyword', insertText: 'INSERT' },
      { label: 'UPDATE', kind: 14, detail: 'SQL keyword', insertText: 'UPDATE' },
      { label: 'DELETE', kind: 14, detail: 'SQL keyword', insertText: 'DELETE' },
      { label: 'JOIN', kind: 14, detail: 'SQL keyword', insertText: 'JOIN' },
      { label: 'LEFT', kind: 14, detail: 'SQL keyword', insertText: 'LEFT' },
      { label: 'RIGHT', kind: 14, detail: 'SQL keyword', insertText: 'RIGHT' },
      { label: 'INNER', kind: 14, detail: 'SQL keyword', insertText: 'INNER' },
      { label: 'OUTER', kind: 14, detail: 'SQL keyword', insertText: 'OUTER' },
      { label: 'ON', kind: 14, detail: 'SQL keyword', insertText: 'ON' },
      { label: 'AND', kind: 14, detail: 'SQL keyword', insertText: 'AND' },
      { label: 'OR', kind: 14, detail: 'SQL keyword', insertText: 'OR' },
      { label: 'ORDER', kind: 14, detail: 'SQL keyword', insertText: 'ORDER' },
      { label: 'BY', kind: 14, detail: 'SQL keyword', insertText: 'BY' },
      { label: 'GROUP', kind: 14, detail: 'SQL keyword', insertText: 'GROUP' },
      { label: 'HAVING', kind: 14, detail: 'SQL keyword', insertText: 'HAVING' },
      { label: 'Users', kind: 6, detail: 'Table: dbo.Users' },
      { label: 'Orders', kind: 6, detail: 'Table: dbo.Orders' },
      { label: 'Id', kind: 5, detail: 'Column: int' },
      { label: 'Name', kind: 5, detail: 'Column: nvarchar(255)' },
      { label: 'Email', kind: 5, detail: 'Column: nvarchar(255)' },
    ];

    // Schema-bus consultation: lets E2E tests inject a fixture
    // SchemaSnapshot via `__TEST_STORES__.schemaBus.injectSnapshotForTest`
    // and have those names surface in Monaco's completion widget without
    // standing up a real cache. The Monaco Provider Orchestration slice's
    // `mockResolver` ALSO consults the bus directly via the orchestrator
    // input deps; this inline consult remains as a backstop for the
    // URI-based connectionId resolution path the orchestrator's React-prop
    // path can't always cover (e.g. when a workspace tab's connectionId
    // prop isn't yet propagated). The downstream orchestrator dedupes by
    // lower-cased label, so the duplication is invisible to Monaco.
    const doc = documents.get(uri);
    if (doc?.connectionId) {
      const snap = getSchemaBusSnapshot(doc.connectionId, doc.database);
      if (snap) {
        for (const t of snap.tables) {
          defaultCompletions.push({
            label: t.name,
            kind: 6,
            detail: `Table: ${t.schema}.${t.name}`,
          });
          for (const col of t.columns) {
            defaultCompletions.push({
              label: col.name,
              kind: 5,
              detail: `Column: ${col.dataType} (${t.name})`,
            });
          }
        }
      }
    }

    return {
      items: defaultCompletions.map((item, index) => ({
        ...item,
        sortText: String(index).padStart(4, '0'),
        data: { position },
      })),
      isIncomplete: false,
    };
  },

  getHoverInfo: async (
    uri: string,
    position: LanguageServicePosition
  ): Promise<{ contents: string } | null> => {
    // Reserved for future use
    void uri;
    void position;
    return null;
  },

  getSignatureHelp: async (
    uri: string,
    position: LanguageServicePosition
  ): Promise<{ signatures: Array<{ label: string }> } | null> => {
    // Reserved for future use
    void uri;
    void position;
    return null;
  },
};

/**
 * Notify all diagnostics listeners
 */
export const notifyDiagnostics = (uri: string, diagnostics: Array<{
  range: { start: { line: number; character: number }; end: { line: number; character: number } };
  message: string;
  severity?: 1 | 2 | 3 | 4;
  source?: string;
  code?: string | number;
}>): void => {
  for (const listener of diagnosticsListeners) {
    listener({ uri, diagnostics });
  }
};
